import { useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { CheckCircle, Circle, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';

const FIELDS = [
  { key: 'glass_ordered', label: 'Glass Ordered', icon: Package },
  { key: 'glass_received', label: 'Glass Received', icon: Package },
  { key: 'profile_ordered', label: 'Profile Ordered', icon: Package },
  { key: 'profile_received', label: 'Profile Received', icon: Package },
];

function pickProcField(procurement, field) {
  const p = procurement || {};
  return {
    done: !!p[field],
    date: p[`${field}_at`],
    by: p[`${field}_by`],
    supplier: p[`${field}_supplier`] || '',
    note: p[`${field}_note`] || '',
  };
}

export function ProcurementTrackingCard({ jobOrder, currentUserRole, onUpdated }) {
  const [editingField, setEditingField] = useState(null); // field key
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const isProcurement = currentUserRole === 'procurement' || currentUserRole === 'admin';
  if (!isProcurement) return null;

  const startEdit = (field) => {
    const current = pickProcField(jobOrder.procurement, field);
    setEditingField(field);
    setSupplier(current.supplier || '');
    setNote(current.note || '');
  };

  const submit = async (field, value) => {
    setSaving(true);
    try {
      await api.post(`/api/job-orders/${encodeURIComponent(jobOrder.jo_number)}/procurement-tracking`, {
        field, value, supplier: supplier.trim(), note: note.trim(),
      });
      toast.success(value ? `${field.replace('_', ' ')} marked done` : `${field.replace('_', ' ')} unmarked`);
      setEditingField(null);
      setSupplier(''); setNote('');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const allDone = FIELDS.every(f => pickProcField(jobOrder.procurement, f.key).done);
  const doneCount = FIELDS.filter(f => pickProcField(jobOrder.procurement, f.key).done).length;

  return (
    <Card data-testid="procurement-tracking-card" className="rounded-sm shadow-sm border-zinc-300">
      <CardHeader className="border-b border-zinc-300">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-xl font-bold text-zinc-950">Procurement Material Tracking</CardTitle>
          <Badge className={allDone
            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
            : 'bg-amber-100 text-amber-800 border border-amber-300'}>
            {doneCount}/{FIELDS.length} complete
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {FIELDS.map((f) => {
          const cur = pickProcField(jobOrder.procurement, f.key);
          const isEditing = editingField === f.key;
          return (
            <div
              key={f.key}
              data-testid={`proc-track-row-${f.key}`}
              className={`border rounded-sm p-3 ${cur.done ? 'bg-emerald-50/40 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="mt-0.5">
                  {cur.done
                    ? <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                    : <Circle size={22} weight="bold" className="text-zinc-400" />}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-bold text-zinc-900">{f.label}</p>
                  {cur.done && (
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {cur.supplier && <>Supplier: <span className="font-bold text-zinc-800">{cur.supplier}</span> · </>}
                      {cur.date && <>Date: <span className="font-bold text-zinc-800">{formatDate(cur.date)}</span> · </>}
                      {cur.by && <>By: <span className="font-bold text-zinc-800">{cur.by}</span></>}
                    </p>
                  )}
                  {cur.note && (
                    <p className="text-xs text-zinc-700 mt-1 italic">Note: {cur.note}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!isEditing && !cur.done && (
                    <Button
                      data-testid={`proc-mark-${f.key}`}
                      onClick={() => startEdit(f.key)}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                    >
                      Mark Done
                    </Button>
                  )}
                  {!isEditing && cur.done && (
                    <Button
                      data-testid={`proc-unmark-${f.key}`}
                      onClick={() => submit(f.key, false)}
                      size="sm"
                      variant="outline"
                      className="h-9"
                      disabled={saving}
                    >
                      Undo
                    </Button>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`proc-supplier-${f.key}`}
                    placeholder="Supplier name (optional)"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Input
                    data-testid={`proc-note-${f.key}`}
                    placeholder="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <div className="md:col-span-2 flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setEditingField(null)} disabled={saving} className="h-9">
                      Cancel
                    </Button>
                    <Button
                      data-testid={`proc-confirm-${f.key}`}
                      size="sm"
                      onClick={() => submit(f.key, true)}
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                    >
                      {saving ? 'Saving...' : 'Confirm Done'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
